import {CommonModule, DOCUMENT} from '@angular/common';
import {
    AfterViewInit,
    Component,
    computed,
    CUSTOM_ELEMENTS_SCHEMA,
    effect,
    EventEmitter,
    Inject,
    input,
    NO_ERRORS_SCHEMA,
    OnInit,
    Output,
    signal
} from '@angular/core';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {sortBy} from 'lodash-es';
import {PortfolioCardComponent} from './portfolio-card/portfolio-card.component';
import {MatIconModule} from '@angular/material/icon';
import {Event} from '@/app/model/interface/event';
import {SortEntry, SortKey} from '@/app/model/common';
import {EventService} from "@/app/services/event.service";
import {CookieService} from "@/app/services/cookie.service";
import {NativeService} from "@/app/services/native.service";
import {useTranslationWithFallback} from "@/app/shared/translation";

@Component({
    selector: "app-sidebar",
    templateUrl: "./sidebar.component.html",
    styleUrls: ["./sidebar.component.scss"],
    imports: [CommonModule, PortfolioCardComponent, MatIconModule, TranslatePipe],
    standalone: true,
    schemas: [
        CUSTOM_ELEMENTS_SCHEMA,
        NO_ERRORS_SCHEMA
    ]
})
export class SidebarComponent implements OnInit, AfterViewInit {
    readonly isOpen = input<boolean>(false);
    readonly eventNode = input<any>(null);
    readonly events = input<Event[]>([]);
    @Output() public close = new EventEmitter<any>();

    private readonly selectedSortKey = signal<SortKey>('performance');

    private readonly sortKeysBySortKey = {
        performance: ['event_performance_percent', 'ems_program_display_label'],
        reduction: ['sum_expected_capacity_value', 'ems_program_display_label'],
        start: ['event_start_dttm_utc', 'ems_program_display_label'],
        end: ['event_end_dttm_utc', 'ems_program_display_label'],
    } as const;

    private readonly sortedEvents = computed<Event[]>(() => {
        this.setTranslations();
        const lodashSortKeys = this.sortKeysBySortKey[this.selectedSortKey()] ?? this.sortKeysBySortKey['performance'];
        return sortBy(this.events() ?? [], lodashSortKeys);
    });

    private readonly buckets = computed(() => {
        const selectedId = this.eventService.selectedEvent()?.event_id;
        return (this.sortedEvents() ?? []).reduce(
            (acc, ev) => {
                const isSelected = selectedId != null && ev.event_id === selectedId;
                switch (ev.event_progress_status) {
                    case 'BEFORE':
                        if (isSelected) acc.selectedInUpcoming = true;
                        acc.upcoming.push(ev);
                        break;
                    case 'DURING':
                        if (isSelected) acc.selectedInCurrent = true;
                        acc.current.push(ev);
                        break;
                    case 'AFTER':
                        if (isSelected) acc.selectedInCompleted = true;
                        acc.completed.push(ev);
                        break;
                }
                return acc;
            },
            {
                upcoming: [] as Event[],
                current: [] as Event[],
                completed: [] as Event[],
                selectedInUpcoming: false,
                selectedInCurrent: false,
                selectedInCompleted: false,
            }
        );
    });

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private translate: TranslateService,
        private readonly eventService: EventService,
        private cookieService: CookieService,
        private readonly nativeService: NativeService,
    ) {
        this.isMobile = this.nativeService.isMobileDevice();
        effect(() => {
            const b = this.buckets();
            this.upcomingEvents = b.upcoming;
            this.currentEvents = b.current;
            this.completedEvents = b.completed;

            const sections = this.eventService.getSidebarSectionsOpen();
            const touched = this.eventService.getSidebarSectionsTouched();

            // For each section:
            // - If touched, respect the stored open/close state
            // - If not touched and contains selected event, auto-expand and mark as touched
            // - If not touched and doesn't contain selected event, leave collapsed

            if (touched.upcoming) {
                this.showUpcoming = sections.upcoming;
            } else if (b.selectedInUpcoming) {
                this.showUpcoming = true;
                this.eventService.setSidebarSectionOpen('upcoming', true);
                this.eventService.markSidebarSectionTouched('upcoming');
            } else {
                this.showUpcoming = false;
            }

            if (touched.current) {
                this.showCurrent = sections.current;
            } else if (b.selectedInCurrent) {
                this.showCurrent = true;
                this.eventService.setSidebarSectionOpen('current', true);
                this.eventService.markSidebarSectionTouched('current');
            } else {
                this.showCurrent = false;
            }

            if (touched.completed) {
                this.showCompleted = sections.completed;
            } else if (b.selectedInCompleted) {
                this.showCompleted = true;
                this.eventService.setSidebarSectionOpen('completed', true);
                this.eventService.markSidebarSectionTouched('completed');
            } else {
                this.showCompleted = false;
            }
        });
    }

    public currentEvents: Event[] = [];
    public upcomingEvents: Event[] = [];
    public completedEvents: Event[] = [];
    public showCurrent: boolean = false;
    public showUpcoming: boolean = false;
    public showCompleted: boolean = false;
    public header: string = "";
    public defaultHeaderContent: boolean = true;
    public entries: SortEntry[] = [];

    isMobile: boolean = false;

    ngOnInit(): void {
        useTranslationWithFallback(this.translate, this.cookieService.getI18NLocale(), this.cookieService.getI18NBrand()).subscribe(() => {
            this.setTranslations();
        });
    }

    setTranslations() {
        this.header = this.translate.instant("DISPATCH_LIST.LIST_TITLE");
        const selectedKey = this.selectedSortKey();
        this.entries = [
            {
                sortKey: 'performance',
                labelContent: this.translate.instant("DISPATCH_LIST.SORT_BY.PERFORMANCE"),
                isSelected: selectedKey === 'performance',
                rightIconName: 'check',
                showRightIcon: selectedKey === 'performance'
            },
            {
                sortKey: 'reduction',
                labelContent: this.translate.instant("DISPATCH_LIST.SORT_BY.REDUCTION"),
                isSelected: selectedKey === 'reduction',
                rightIconName: 'check',
                showRightIcon: selectedKey === 'reduction'
            },
            {
                sortKey: 'start',
                labelContent: this.translate.instant("DISPATCH_LIST.SORT_BY.START"),
                isSelected: selectedKey === 'start',
                rightIconName: 'check',
                showRightIcon: selectedKey === 'start'
            },
            {
                sortKey: 'end',
                labelContent: this.translate.instant("DISPATCH_LIST.SORT_BY.END"),
                isSelected: selectedKey === 'end',
                rightIconName: 'check',
                showRightIcon: selectedKey === 'end'
            },
        ];
    }

    ngAfterViewInit(): void {
        this.applyDropdownStyles(); // apply initially

        const dropdownButton = this.document.querySelector('.button-style > button:nth-child(1)');
        dropdownButton?.addEventListener('click', () => {
            setTimeout(() => this.applyDropdownStyles(), 10);
        });

        // Re-apply styles after any click on the dropdown (e.g. open, toggle, interaction)
        const hostEl = this.document.querySelector('dsy-input-dropdown');
        hostEl?.addEventListener('click', () => {
            setTimeout(() => this.applyDropdownStyles(), 10);
        });

        if (!this.isMobile) {
            const sidebarMenu = this.document.querySelector('dsy-sidebar');
            const shadowRoot = sidebarMenu?.shadowRoot
            if (!shadowRoot) return;

            this.appendStyle(shadowRoot, `
        .sidebar-main-container {
          margin-top: 4rem;
          padding-bottom: 4rem;
        }
      `)
        }
    }

    private applyDropdownStyles(): void {
        const hostEl = this.document.querySelector('dsy-input-dropdown');
        const shadowRoot = hostEl?.shadowRoot;
        if (!shadowRoot) return;

        const listWrapper = shadowRoot
            .querySelector('.input-dropdown-container')
            ?.querySelector('#dropdown-wrapper') as HTMLElement;

        if (!listWrapper) return;

        this.appendStyle(shadowRoot, `
        .input-dropdown-container {
          width: 326px;
        }
      `)

        Object.assign(listWrapper.style, {
            width: '100%',
            border: '1px solid #667790',
            borderRadius: '0.375rem',
            padding: '10px 0',
            marginTop: '10px',
            background: 'white',
        });

        const innerShadowRoot = listWrapper.querySelector('#dropdown-list')?.shadowRoot;
        if (!innerShadowRoot) return;

        this.appendStyle(innerShadowRoot, `
          .list-container {
            min-height: unset !important;
            box-shadow: none !important;
          }
          span {
            font-weight: 400 !important;
            font-size: 16px !important;
            line-height: 16px !important;
          }`
        );

        const listContainer = innerShadowRoot.querySelector('.list-container');
        const nodeList = listContainer?.querySelectorAll('dsy-list-element');
        nodeList?.forEach(node => {
            const itemShadow = node.shadowRoot;
            if (!itemShadow) return;

            const iconShadow = itemShadow.querySelector('.list-element')
                ?.querySelector('.right-hand-content')
                ?.querySelector('dsy-icon')?.shadowRoot;

            this.appendStyle(itemShadow, `
                .list-element:hover {
                  background: #F7F8FB !important;
                  color: var(--link-color, #5738FF);
                }
                .selected {
                  background: #F7F8FB !important;
                  color: var(--link-color, #5738FF) !important;
                }`
            );

            if (!iconShadow) return;

            this.appendStyle(iconShadow, `
                .icon {
                  width: 1.7rem !important;
                  height: 1.7rem !important;
                }`
            )
        });

        const inputWrapper = shadowRoot
            .querySelector('.input-dropdown-container')
            ?.querySelector('#dropdown-input')?.shadowRoot?.querySelector('dsy-input')
            ?.shadowRoot;

        const icon = shadowRoot
            .querySelector('.input-dropdown-container')
            ?.querySelector('#dropdown-input')
            ?.querySelector('.icon-and-counter-container')
            ?.querySelector('dsy-icon')?.shadowRoot

        if (!inputWrapper || !icon) return;

        this.appendStyle(inputWrapper, `
        .input-wrapper {
          width: 326px !important;
          height: 40px !important;
        }
        .label {
          font-size: 12px !important;
          line-height: 12px !important;
        }
        .focused {
          font-size: 14px !important;
          line-height: 14px !important;
        }
        .input-div {
          margin-left: 0 !important;
          margin-right: 0 !important;
        }
      `)

        this.appendStyle(icon, `
        .icon {
          height: 16px !important;
          width: 16px !important;
        }
      `)
    }

    private appendStyle(shadowRoot: ShadowRoot, css: string): void {
        const existing = Array.from(shadowRoot.querySelectorAll('style')).find(style =>
            style.textContent?.includes(css.trim().slice(0, 10)) // crude but effective duplicate check
        );
        if (existing) return;

        const style = this.document.createElement('style');
        style.textContent = css;
        shadowRoot.appendChild(style);
    }

    handleOpenCloseInteraction = (value: any) => {
        this.handleCancelButtonInteraction(value);
    };

    handleCancelButtonInteraction = (value: any) => {
        this.close.emit(value);
    };

    private syncSectionVisibilityFromService(): void {
        const sections = this.eventService.getSidebarSectionsOpen();
        this.showCurrent = sections.current;
        this.showUpcoming = sections.upcoming;
        this.showCompleted = sections.completed;
    }

    toggleSection(section: 'current' | 'upcoming' | 'completed'): void {
        this.eventService.toggleSidebarSection(section);
        this.syncSectionVisibilityFromService();
    }

    closeOpenCurrent() { this.toggleSection('current'); }
    closeOpenUpcoming() { this.toggleSection('upcoming'); }
    closeOpenCompleted() { this.toggleSection('completed'); }

    onSortChange = (labelContent: string): void => {
        this.selectedSortKey.set(this.entries.find(value => value.labelContent === labelContent)?.sortKey || 'performance');
    };
}
